<script setup lang="ts">
import { computed, ref } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Copy, GitBranch, Music, ListMusic, Link2Off } from 'lucide-vue-next'
import SceneDisplayConfig from '@/components/scenes/SceneDisplayConfig.vue'
import NodeFrame from './NodeFrame.vue'
import type { Scene } from '@/stores/scenes'
import type { Display } from '@/stores/displays'
import type { MusicNode } from '@/stores/musicNodes'
import type { PlaylistNode } from '@/stores/playlistNodes'

interface Props {
  scene: Scene
  preview?: { display: Display } | null
  isMobile?: boolean
  allScenes?: Scene[]
  allMusicNodes?: MusicNode[]
  allPlaylistNodes?: PlaylistNode[]
  linkedMusicNodeIds?: string[]
  linkedPlaylistNodeIds?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  allScenes: () => [],
  allMusicNodes: () => [],
  allPlaylistNodes: () => [],
  linkedMusicNodeIds: () => [],
  linkedPlaylistNodeIds: () => []
})

// Search queries for submenus
const parentSearchQuery = ref('')
const musicSearchQuery = ref('')
const playlistSearchQuery = ref('')

// Fuzzy search helper
const fuzzyMatch = (query: string, text: string): boolean => {
  if (!query) return true
  const lowerQuery = query.toLowerCase()
  const lowerText = text.toLowerCase()
  return lowerText.includes(lowerQuery)
}

// Filtered lists based on search
const filteredScenes = computed(() => {
  // Exclude the current scene and its descendants to prevent cycles
  return props.allScenes.filter(s => 
    s.id !== props.scene.id && 
    s.id !== props.scene.parent_id &&
    fuzzyMatch(parentSearchQuery.value, s.name)
  )
})

const filteredMusicNodes = computed(() => {
  return props.allMusicNodes.filter(m => 
    !props.linkedMusicNodeIds.includes(m.id) &&
    fuzzyMatch(musicSearchQuery.value, m.name)
  )
})

const filteredPlaylistNodes = computed(() => {
  return props.allPlaylistNodes.filter(p => 
    !props.linkedPlaylistNodeIds.includes(p.id) &&
    fuzzyMatch(playlistSearchQuery.value, p.name)
  )
})

// Check if scene has a parent that can be removed
const hasParent = computed(() => !!props.scene.parent_id)

// Get current parent name
const currentParentName = computed(() => {
  if (!props.scene.parent_id) return null
  const parent = props.allScenes.find(s => s.id === props.scene.parent_id)
  return parent?.name || 'Unknown'
})

const emit = defineEmits<{
  activate: [sceneId: string]
  createChild: [sceneId: string]
  edit: [scene: Scene]
  duplicate: [scene: Scene]
  delete: [scene: Scene]
  setParent: [sceneId: string, parentId: string | null]
  linkMusic: [sceneId: string, musicNodeId: string]
  linkPlaylist: [sceneId: string, playlistNodeId: string]
}>()

// Preview width based on node size (260px - 24px padding = 236px)
const previewMaxWidth = computed(() => props.isMobile ? 156 : 236)

// Expose Position enum values for template
const handlePositions = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right
}
</script>

<template>
  <!-- Source handle (top) - for children to connect TO this node -->
  <Handle type="source" :position="handlePositions.top" />
  
  <!-- Target handle (bottom) - for this node to connect to parents -->
  <Handle type="target" :position="handlePositions.bottom" />
  
  <!-- Target handle (right) - for audio nodes to connect to this scene -->
  <Handle 
    type="target" 
    :position="handlePositions.right" 
    id="audio-in"
    class="audio-handle"
  />
  
  <div 
    class="scene-node-wrapper"
    :class="{ 
      'scene-node-mobile': isMobile,
      'scene-node-active': scene.is_active
    }"
  >
    <NodeFrame 
      :id="`scene-${scene.id}`"
      variant="scene"
      :is-active="scene.is_active"
      :show-glow="scene.is_active"
    >
      <div 
        class="scene-node-content"
        @click="emit('activate', scene.id)"
      >
        <!-- Display Preview Section -->
        <div 
          v-if="preview?.display && scene?.id" 
          class="scene-node-preview"
        >
          <SceneDisplayConfig
            :key="`${scene.id}-${preview.display.id}`"
            :scene-id="scene.id"
            :display="preview.display"
            :preview-max-width="previewMaxWidth"
            eager
          />
        </div>
        
        <!-- Scene Info -->
        <div class="scene-node-header">
          <span class="scene-node-name">{{ scene.name }}</span>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                variant="ghost"
                size="icon"
                class="scene-node-menu-button"
                @click.stop
              >
                <MoreHorizontal class="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-56">
              <DropdownMenuItem @click.stop="emit('edit', scene)">
                <Pencil class="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem @click.stop="emit('duplicate', scene)">
                <Copy class="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              <!-- Set Parent Submenu -->
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <GitBranch class="h-4 w-4 mr-2" />
                  <span>Set Parent</span>
                  <span v-if="currentParentName" class="ml-auto text-xs text-muted-foreground truncate max-w-[80px]">
                    {{ currentParentName }}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent class="w-64">
                  <div class="p-2" @click.stop>
                    <Input
                      v-model="parentSearchQuery"
                      placeholder="Search scenes..."
                      class="h-8 text-sm"
                      @click.stop
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <div class="max-h-48 overflow-y-auto">
                    <!-- Remove parent option -->
                    <DropdownMenuItem 
                      v-if="hasParent"
                      @click.stop="emit('setParent', scene.id, null)"
                      class="text-muted-foreground"
                    >
                      <Link2Off class="h-4 w-4 mr-2" />
                      Remove parent
                    </DropdownMenuItem>
                    <DropdownMenuSeparator v-if="hasParent && filteredScenes.length > 0" />
                    <!-- Scene options -->
                    <DropdownMenuItem 
                      v-for="s in filteredScenes.slice(0, 10)"
                      :key="s.id"
                      @click.stop="emit('setParent', scene.id, s.id)"
                    >
                      <GitBranch class="h-4 w-4 mr-2 opacity-50" />
                      {{ s.name }}
                    </DropdownMenuItem>
                    <div v-if="filteredScenes.length === 0 && !hasParent" class="px-2 py-1.5 text-sm text-muted-foreground">
                      No scenes found
                    </div>
                    <div v-if="filteredScenes.length > 10" class="px-2 py-1.5 text-xs text-muted-foreground">
                      +{{ filteredScenes.length - 10 }} more...
                    </div>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              
              <!-- Link Music Submenu -->
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Music class="h-4 w-4 mr-2" />
                  <span>Link Music</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent class="w-64">
                  <div class="p-2" @click.stop>
                    <Input
                      v-model="musicSearchQuery"
                      placeholder="Search music..."
                      class="h-8 text-sm"
                      @click.stop
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <div class="max-h-48 overflow-y-auto">
                    <DropdownMenuItem 
                      v-for="m in filteredMusicNodes.slice(0, 10)"
                      :key="m.id"
                      @click.stop="emit('linkMusic', scene.id, m.id)"
                    >
                      <Music class="h-4 w-4 mr-2 opacity-50" />
                      {{ m.name }}
                    </DropdownMenuItem>
                    <div v-if="filteredMusicNodes.length === 0" class="px-2 py-1.5 text-sm text-muted-foreground">
                      No music nodes available
                    </div>
                    <div v-if="filteredMusicNodes.length > 10" class="px-2 py-1.5 text-xs text-muted-foreground">
                      +{{ filteredMusicNodes.length - 10 }} more...
                    </div>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              
              <!-- Link Playlist Submenu -->
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <ListMusic class="h-4 w-4 mr-2" />
                  <span>Link Playlist</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent class="w-64">
                  <div class="p-2" @click.stop>
                    <Input
                      v-model="playlistSearchQuery"
                      placeholder="Search playlists..."
                      class="h-8 text-sm"
                      @click.stop
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <div class="max-h-48 overflow-y-auto">
                    <DropdownMenuItem 
                      v-for="p in filteredPlaylistNodes.slice(0, 10)"
                      :key="p.id"
                      @click.stop="emit('linkPlaylist', scene.id, p.id)"
                    >
                      <ListMusic class="h-4 w-4 mr-2 opacity-50" />
                      {{ p.name }}
                    </DropdownMenuItem>
                    <div v-if="filteredPlaylistNodes.length === 0" class="px-2 py-1.5 text-sm text-muted-foreground">
                      No playlists available
                    </div>
                    <div v-if="filteredPlaylistNodes.length > 10" class="px-2 py-1.5 text-xs text-muted-foreground">
                      +{{ filteredPlaylistNodes.length - 10 }} more...
                    </div>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem 
                @click.stop="emit('delete', scene)"
                class="text-destructive focus:text-destructive"
              >
                <Trash2 class="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div v-if="scene.description && !isMobile" class="scene-node-description">
          {{ scene.description }}
        </div>
      </div>
    </NodeFrame>
  </div>
</template>

<style scoped>
.scene-node-wrapper {
  width: 260px;
  min-height: 200px;
  cursor: pointer;
  transition: transform 200ms ease;
}

.scene-node-mobile {
  width: 180px;
  min-height: 140px;
}

.scene-node-wrapper:hover {
  transform: translateY(-2px);
}

/* Disable hover effects on mobile for better touch experience */
@media (hover: none) {
  .scene-node-wrapper:hover {
    transform: none;
  }
}

/* Content */
.scene-node-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px;
  padding-bottom: 0;
}

.scene-node-preview {
  width: 100%;
  overflow: hidden;
  background: hsl(var(--muted));
  border: 1px solid hsl(var(--primary) / 0.3);
}

/* Let DisplayPreview control its own sizing based on aspect ratio */
.scene-node-preview :deep([data-testid="display-preview"]) {
  display: flex;
  justify-content: center;
}

.scene-node-preview :deep([data-testid="display-preview"] > div) {
  border-radius: 0 !important;
}

/* Separator line between preview and header */
.scene-node-preview + .scene-node-header {
  border-top: 1px solid hsl(var(--primary) / 0.3);
  margin-top: 4px;
}

.scene-node-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 6px 8px 8px;
}

.scene-node-mobile .scene-node-header {
  padding: 4px 6px 6px;
}

.scene-node-name {
  font-weight: 600;
  font-size: 14px;
  color: hsl(var(--foreground));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  max-width: calc(100% - 40px);
}

.scene-node-mobile .scene-node-name {
  font-size: 12px;
}

.scene-node-menu-button {
  position: absolute;
  right: 8px;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  opacity: 0;
  transition: opacity 150ms ease;
}

.scene-node-wrapper:hover .scene-node-menu-button,
.scene-node-menu-button:focus,
.scene-node-menu-button[data-state="open"] {
  opacity: 1;
}

.scene-node-description {
  margin-top: 4px;
  padding: 0 16px 12px;
  font-size: 12px;
  color: hsl(var(--muted-foreground));
  line-height: 1.4;
}

/* Audio handle styling */
.audio-handle {
  background: hsl(var(--primary)) !important;
}

:deep(.vue-flow__handle.audio-handle) {
  width: 8px;
  height: 8px;
  background: hsl(var(--primary));
}
</style>
